import NeptuneElement from '../../NeptuneElement.js';

class HeaderActionItem extends NeptuneElement {

    templateMarkup = `
        <slot name="header-items"></slot>
        <neptune-menu id="headerMenu"></neptune-menu>
    `;

    componentCssUrl = '/src/components/chrome/headerActionItems/header-action-item.css';

    constructor() {
        super();
        this.items = [];
        this.currentMenuType = null;
        this.iconClickHandler = null;
        this.isMenuOpen = false;
        this.lastTriggerElement = null;
    }

    async connectedCallback() {
        await super.connectedCallback();

        const itemsAttr = this.getAttribute('items');
        if (itemsAttr) {
            try {
                this.items = JSON.parse(itemsAttr);
            } catch (error) {
                console.error('Invalid items JSON:', error);
            }
        }
    }

    static get observedAttributes() {
        return ['items'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) return;

        if (name === 'items') {
            try {
                this.items = JSON.parse(newValue || '[]');
            } catch (error) {
                console.error('Invalid items JSON:', error);
            }
        }
    }

    renderMenuItems() {
        if (!this.isRendered) return;

        const neptuneMenu = this.shadowRoot?.querySelector('#headerMenu');
        if (!neptuneMenu) return;

        neptuneMenu.innerHTML = '';

        if (!this.items || this.items.length === 0) {
            const li = document.createElement('li');
            li.textContent = 'No items available';
            li.setAttribute('slot', 'menu-item');
            neptuneMenu.appendChild(li);
            return;
        }

        this.items.forEach(item => {
            const li = document.createElement('li');
            li.setAttribute('slot', 'menu-item');
            li.setAttribute('role', 'menuitem');
            li.classList.add('menu-item');

            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = item.name || item.title;
            button.dataset.route = item.route || '';
            button.dataset.id = item.id || '';

            if (item.selected) {
                button.dataset.selected = 'true';
            }

            li.appendChild(button);
            neptuneMenu.appendChild(li);
        });
    }

    async setEventListeners() {
        if (!this.isRendered) return;

        const iconSlot = this.shadowRoot?.querySelector('slot[name="header-items"]');
        if (iconSlot) {
            iconSlot.addEventListener('slotchange', () => {
                this.attachIconTriggerListeners();
            });
            this.attachIconTriggerListeners();
        }

        const neptuneMenu = this.shadowRoot?.querySelector('#headerMenu');
        if (neptuneMenu) {
            neptuneMenu.addEventListener('click', (e) => {
                const button = e.target.closest('button');
                if (button) {
                    const itemData = {
                        id: button.dataset.id,
                        name: button.textContent.trim(),
                        route: button.dataset.route                       
                    };
                    this.dispatchEvent(new CustomEvent('item-select', {
                        bubbles: true,
                        composed: true,
                        detail: { item: itemData }
                    }));

                    this.closeMenu();
                }
            });
        }
    }

    attachIconTriggerListeners() {
        const triggerElements = this.querySelectorAll('[slot="header-items"]');
        triggerElements.forEach((element) => {
            if (this.iconClickHandler) {
                element.removeEventListener('click', this.iconClickHandler);
            }
            this.iconClickHandler = (e) => {
                e.stopPropagation();
                if (this.isMenuOpen && this.lastTriggerElement === element) {
                    this.closeMenu();
                } else {
                    this.openMenu(element);
                }
            };
            element.addEventListener('click', this.iconClickHandler);
        });
    }

    openMenu(triggerElement) {
        const neptuneMenu = this.shadowRoot?.querySelector('#headerMenu');
        if (!neptuneMenu) return;

        this.renderMenuItems();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const triggerRect = triggerElement.getBoundingClientRect();
        const menuWidth = 220;
        const menuMaxHeight = 400;
        const gap = 8;

        let top = triggerRect.bottom + gap;
        const availableHeightBelow = viewportHeight - top;
        if (availableHeightBelow < 100 && triggerRect.top > 200) {
            top = triggerRect.top - Math.min(menuMaxHeight, 200) - gap;
        }

        let left;
        if (triggerRect.right > viewportWidth * 0.75) {
            left = triggerRect.right - menuWidth;
        } else if (triggerRect.left < viewportWidth * 0.25) {
            left = triggerRect.left;
        } else {
            left = triggerRect.left + (triggerRect.width / 2) - (menuWidth / 2);
        }

        if (left < gap) {
            left = gap;
        } else if (left + menuWidth > viewportWidth - gap) {
            left = viewportWidth - menuWidth - gap;
        }

        neptuneMenu.style.position = 'fixed';
        neptuneMenu.style.top = `${Math.max(gap, top)}px`;
        neptuneMenu.style.left = `${left}px`;
        neptuneMenu.style.zIndex = '1001';
        neptuneMenu.style.setProperty('--menu-top', `${Math.max(gap, top)}px`);
        neptuneMenu.style.setProperty('--menu-left', `${left}px`);
        
        neptuneMenu.triggerElement = null;
        neptuneMenu.setAttribute('open', '');
        
        if (neptuneMenu.updatePosition) {
            neptuneMenu.originalUpdatePosition = neptuneMenu.updatePosition.bind(neptuneMenu);
            neptuneMenu.updatePosition = () => {};
        }
        
        setTimeout(() => {
            neptuneMenu.style.position = 'fixed';
            neptuneMenu.style.top = `${Math.max(gap, top)}px`;
            neptuneMenu.style.left = `${left}px`;
            neptuneMenu.style.zIndex = '1001';
            
            if (neptuneMenu.triggerElement) {
                neptuneMenu.triggerElement = null;
            }
        }, 0);
        
        this.isMenuOpen = true;
        this.lastTriggerElement = triggerElement;

        setTimeout(() => {
            document.addEventListener('click', this.handleOutsideClick.bind(this), { once: true });
        }, 0);

        this.dispatchEvent(new CustomEvent('menu-opened', {
            bubbles: true,
            composed: true,
            detail: { triggerElement }
        }));
    }

    handleOutsideClick(event) {
        const neptuneMenu = this.shadowRoot?.querySelector('#headerMenu');
        if (!neptuneMenu || !this.isMenuOpen) return;

        const clickedInsideMenu = event.composedPath().includes(neptuneMenu);
        const clickedTrigger = this.lastTriggerElement && event.composedPath().includes(this.lastTriggerElement);
        
        if (!clickedInsideMenu && !clickedTrigger) {
            this.closeMenu();
        }
    }

    closeMenu() {
        const neptuneMenu = this.shadowRoot?.querySelector('#headerMenu');
        if (neptuneMenu) {
            neptuneMenu.removeAttribute('open');
            neptuneMenu.style.removeProperty('--menu-top');
            neptuneMenu.style.removeProperty('--menu-left');
            neptuneMenu.style.position = '';
            neptuneMenu.style.top = '';
            neptuneMenu.style.left = '';
            neptuneMenu.style.zIndex = '';
            
            if (neptuneMenu.originalUpdatePosition) {
                neptuneMenu.updatePosition = neptuneMenu.originalUpdatePosition;
                neptuneMenu.originalUpdatePosition = null;
            }
        }
        
        this.isMenuOpen = false;
        this.lastTriggerElement = null;

        this.dispatchEvent(new CustomEvent('menu-closed', {
            bubbles: true,
            composed: true
        }));
    }

    async removeEventListeners() {
        if (this.iconClickHandler) {
            const triggerElements = this.querySelectorAll('[slot="header-items"]');
            triggerElements.forEach(element => {
                element.removeEventListener('click', this.iconClickHandler);
            });
        }
        
        document.removeEventListener('click', this.handleOutsideClick.bind(this));
        
        if (this.isMenuOpen) {
            this.closeMenu();
        }
    }
}

if (!customElements.get('neptune-header-action-item')) {
    customElements.define('neptune-header-action-item', HeaderActionItem);
}

export default HeaderActionItem;
